import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Badge,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  MedicalServices as MedicalServicesIcon,
  LocalHospital as HospitalIcon,
  Receipt as ReceiptIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Business as BusinessIcon,
  Medication as MedicationIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Factory,
  ReceiptLong as AuditLogIcon,
  FilterAlt as FilterAltIcon,
  NoteAlt as ProgressNoteIcon,
  Checklist as ChecklistIcon,
  AccessTime as AttendanceIcon,
  LocalPharmacy,
  StickyNote2 as NoteIcon,
  AssignmentTurnedIn as TaskIcon,
  Engineering as EngineeringIcon,
  SupportAgent as CRMIcon,
  ConfirmationNumber as TicketIcon,
  ContactPage as LeadIcon,
  Groups as CustomerIcon,
  Description as DocumentIcon,
  BarChart as ReportIcon,
  SwapHoriz as SwapHorizIcon,
  CalendarMonth as RosterIcon,
  BeachAccess as LeaveMenuIcon,
  Payments as PayrollMenuIcon,
  Assignment as AssignmentIcon,
  HelpOutline as HelpIcon,
} from "@mui/icons-material";
import { useNavigation } from "../contexts/NavigationContext";
import { useTenant } from "../contexts/TenantContext";
import { useAuth } from "../contexts/AuthContext";
import facilityService from "../services/facilityService";
import TenantSelector from "../components/TenantSelector";
import CareXLogo from "../components/CareXLogo";
import NotificationDropdown from "../components/NotificationDropdown";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import { canAccessBilling as canAccessBillingForRole } from "../pages/billing/billingConfig";

interface AppLayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  submenu?: MenuItem[];
}

const drawerWidth = 280;
const collapsedWidth = 70;

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Phones use the slide-out drawer with full labels; tablets start with the icon-only sidebar
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
      setSidebarCollapsed(false);
    } else if (isTablet) {
      setSidebarCollapsed(true);
    }
  }, [isMobile, isTablet]);
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedMenus, setExpandedMenus] = useState<{
    [key: string]: boolean;
  }>({});
  const notifOpen = Boolean(notifAnchorEl);
  const { unreadNotifications } = useNavigation();
  const { tenant, currentTenantId } = useTenant();
  const location = useLocation();

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // ── Facility logo ──────────────────────────────────────────────────────────
  const [facilityLogoUrl, setFacilityLogoUrl] = React.useState<string | null>(null);
  const currentBlobUrlRef = React.useRef<string | null>(null);

  const fetchFacilityLogo = React.useCallback(async () => {
    const facilityId = user?.facilityId || currentTenantId;
    if (!facilityId) {
      setFacilityLogoUrl(null);
      return;
    }
    const url = await facilityService.getFacilityLogoBlob(facilityId);
    // Revoke old blob URL before setting new one
    if (currentBlobUrlRef.current) {
      URL.revokeObjectURL(currentBlobUrlRef.current);
    }
    currentBlobUrlRef.current = url;
    setFacilityLogoUrl(url);
  }, [user?.facilityId, currentTenantId]);

  useEffect(() => {
    fetchFacilityLogo();
    // Re-fetch after logo upload (Settings dispatches this event)
    window.addEventListener("facilitiesUpdated", fetchFacilityLogo);
    return () => {
      window.removeEventListener("facilitiesUpdated", fetchFacilityLogo);
      if (currentBlobUrlRef.current) URL.revokeObjectURL(currentBlobUrlRef.current);
    };
  }, [fetchFacilityLogo]);
  // ──────────────────────────────────────────────────────────────────────────


  const handleLogout = async () => {
    await logout();
    localStorage.removeItem("currentTenantId");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleProfileClick = () => {
    navigate("/Profile");
  };

  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const isClientAdmin = user?.role === "CLIENT_ADMIN";
  const isAdmin = user?.role === "ADMIN";
  const isFacilityAdmin = user?.role === "FACILITY_ADMIN";
  const isNurse = user?.role === "NURSE" || user?.role === "CAREGIVER" || user?.role === "LEAD_CAREGIVER";
  const canAccessBilling = canAccessBillingForRole(user?.role);

  // Nested pages (e.g. /billing/new/CMS_1500) keep their parent menu item highlighted
  const isActivePath = (path?: string) =>
    !!path && (location.pathname === path || location.pathname.startsWith(`${path}/`));

  let menuItems: MenuItem[] = [];

  if (isClientAdmin) {
    menuItems = [
      { text: "Dashboard", icon: <DashboardIcon />, path: "/client/dashboard" },
      { text: "My Facilities", icon: <BusinessIcon />, path: "/tenant-management" },
      { text: "Residents", icon: <PeopleIcon />, path: "/residents" },
      { text: "Shift Handover", icon: <SwapHorizIcon />, path: "/care/handover" },
      { text: "Medication", icon: <AssignmentIcon />, path: "/progress-notes" },
      { text: "Medication Schedule", icon: <MedicationIcon />, path: "/medication-schedule" },
      {
        text: "Documentation",
        icon: <DocumentIcon />,
        path: "/documentation",
      },
      {
        text: "Attendance",
        icon: <AttendanceIcon />,
        path: "/attendance-management",
      },
      { text: "Users", icon: <PeopleIcon />, path: "/admin/users" },
      { text: "Care Requests", icon: <TicketIcon />, path: "/care/requests" },
      ...(canAccessBilling
        ? [
          {
            text: "Billing",
            icon: <ReceiptIcon />,
            path: "/billing",
          }
        ]
        : []),
      ...(isAdmin || isSuperAdmin || isFacilityAdmin
        ? [
          {
            text: "Reports",
            icon: <ReportIcon />,
            path: "/reports",
          }
        ]
        : []),
      {
        text: "Settings",
        icon: <SettingsIcon />,
        path: "/settings",
      },
    ];
  } else {
    menuItems = [
      { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
      { text: "Residents", icon: <PeopleIcon />, path: "/residents" },
      { text: "Medication Schedule", icon: <MedicationIcon />, path: "/medication-schedule" },
      { text: "Notes", icon: <ProgressNoteIcon />, path: "/resident-progress-notes" },
      ...(!isNurse ? [{ text: "Order Medication", icon: <MedicationIcon />, path: "/medication-orders" }] : []),
      ...(isAdmin || isSuperAdmin
        ? [
          { text: "Attendance", icon: <AttendanceIcon />, path: "/attendance-management" },
          { text: "Audit Logs", icon: <AuditLogIcon />, path: "/audit-logs" },
        ]
        : []),
      { text: "Care Requests", icon: <TicketIcon />, path: "/care/requests" },
      { text: "Shift Handover", icon: <SwapHorizIcon />, path: "/care/handover" },
      ...(canAccessBilling
        ? [
          { text: "Billing", icon: <ReceiptIcon />, path: "/billing" },
        ]
        : []),
      ...(isAdmin || isFacilityAdmin || isSuperAdmin
        ? [
          { text: "Reports", icon: <ReportIcon />, path: "/reports" },
        ]
        : []),
      ...(isAdmin || isFacilityAdmin || isSuperAdmin || isClientAdmin
        ? [
          { text: "Settings", icon: <SettingsIcon />, path: "/settings" }
        ]
        : []),
      /*
      {
        text: "Super Admin",
        icon: <EngineeringIcon />,
        submenu: [
          { text: "Dashboard", icon: <DashboardIcon />, path: "/super-admin/dashboard" },
          { text: "Clients", icon: <PeopleIcon />, path: "/super-admin/clients" },
          { text: "Facilities", icon: <HospitalIcon />, path: "/tenant-management" },
          { text: "Users", icon: <PeopleIcon />, path: "/admin/users" },
        ]
      }
      */
    ];
  }

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const toggleSubmenu = (menuText: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuText]: !prev[menuText],
    }));
  };

  const renderMenuItems = (items: MenuItem[], isSubmenu = false) => {
    return items.map((item) => (
      <Box key={item.text}>
        {item.submenu ? (
          <>
            <Tooltip
              title={sidebarCollapsed ? item.text : ""}
              placement="right"
            >
              <ListItem
                onClick={() => toggleSubmenu(item.text)}
                sx={{
                  backgroundColor: expandedMenus[item.text]
                    ? "rgba(0, 119, 182, 0.08)"
                    : "transparent",
                  color: expandedMenus[item.text] ? "#0077B6" : theme.palette.text.primary,
                  borderRadius: sidebarCollapsed ? "8px" : "0px 8px 8px 0px",
                  borderLeft: expandedMenus[item.text] && !sidebarCollapsed ? "3px solid #0077B6" : "3px solid transparent",
                  mx: sidebarCollapsed ? 0.5 : 0,
                  my: 0.5,
                  px: sidebarCollapsed ? 1.5 : 2,
                  py: 1.25,
                  transition: "all 0.2s ease",
                  justifyContent: sidebarCollapsed ? "center" : "space-between",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(0, 119, 182, 0.06)",
                    color: "#0077B6",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ListItemIcon
                    sx={{
                      color: expandedMenus[item.text] ? "#0077B6" : theme.palette.text.secondary,
                      minWidth: sidebarCollapsed ? "auto" : 40,
                      mr: sidebarCollapsed ? 0 : 2,
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        fontWeight: 500,
                        "& .MuiTypography-root": {
                          fontSize: "0.95rem",
                        },
                      }}
                    />
                  )}
                </Box>
                {!sidebarCollapsed &&
                  (expandedMenus[item.text] ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  ))}
              </ListItem>
            </Tooltip>
            <Collapse
              in={expandedMenus[item.text] || false}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                {renderMenuItems(item.submenu, true)}
              </List>
            </Collapse>
          </>
        ) : (
          <Tooltip title={sidebarCollapsed ? item.text : ""} placement="right">
            <ListItem
              component={Link}
              to={item.path || "#"}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                backgroundColor:
                  isActivePath(item.path)
                    ? "rgba(0, 119, 182, 0.08)"
                    : "transparent",
                borderLeft:
                  isActivePath(item.path) && !sidebarCollapsed
                    ? `3px solid #0077B6`
                    : "3px solid transparent",
                color:
                  isActivePath(item.path)
                    ? "#0077B6"
                    : theme.palette.text.primary,
                borderRadius: sidebarCollapsed
                  ? "8px"
                  : isSubmenu
                    ? "0px 8px 8px 0px"
                    : "0px 8px 8px 0px",
                mx: sidebarCollapsed ? 0.5 : isSubmenu ? 1 : 0,
                my: 0.25,
                px: sidebarCollapsed ? 1.5 : isSubmenu ? 4 : 2,
                py: 1.25,
                transition: "all 0.2s ease",
                justifyContent: sidebarCollapsed ? "center" : "flex-start",
                "&:hover": {
                  backgroundColor: "rgba(0, 119, 182, 0.06)",
                  color: "#0077B6",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color:
                    isActivePath(item.path)
                      ? "#0077B6"
                      : theme.palette.text.secondary,
                  minWidth: sidebarCollapsed ? "auto" : 40,
                  mr: sidebarCollapsed ? 0 : 2,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!sidebarCollapsed && (
                <ListItemText
                  primary={item.text}
                  sx={{
                    fontWeight: isActivePath(item.path) ? 600 : 500,
                    "& .MuiTypography-root": {
                      fontSize: "0.95rem",
                    },
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        )}
      </Box>
    ));
  };

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Teal accent strip at the very top */}
      <Box sx={{ height: "3px", background: "linear-gradient(90deg, #0077B6 0%, #00B4D8 100%)", flexShrink: 0 }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: sidebarCollapsed ? "column" : "row",
          alignItems: "center",
          justifyContent: sidebarCollapsed ? "center" : "space-between",
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E2E8F0",
          color: "#1A2B45",
          p: sidebarCollapsed ? 1 : 2,
          py: sidebarCollapsed ? 1.5 : 2,
          minHeight: 67,
          gap: sidebarCollapsed ? 1 : 0,
        }}
      >
        <Box sx={{
          display: "flex",
          alignItems: "center",
          flex: sidebarCollapsed ? "0 1 auto" : 1,
          justifyContent: sidebarCollapsed ? "center" : "flex-start",
          overflow: "hidden",
          minWidth: 0,
        }}>
          <CareXLogo
            style={{
              height: "auto",
              width: sidebarCollapsed ? "56px" : "auto",
              maxWidth: sidebarCollapsed ? "100%" : "180px",
              maxHeight: sidebarCollapsed ? "none" : "44px",
              flexShrink: 0,
            }}
          />
        </Box>

        {!isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{ color: "#5C6F85", "&:hover": { backgroundColor: "rgba(0,119,182,0.06)", color: "#0077B6" } }}
            size="small"
          >
            {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
        {isMobile && (
          <IconButton
            onClick={() => setMobileOpen(false)}
            sx={{ color: "#5C6F85", ml: sidebarCollapsed ? 0 : 1 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <>
        <Box sx={{ p: sidebarCollapsed ? 0.5 : 1 }}>
          <TenantSelector collapsed={sidebarCollapsed} />
        </Box>
        <Divider />
      </>

      <List sx={{ flex: 1, overflow: "auto", p: sidebarCollapsed ? 0.5 : 1 }}>
        {renderMenuItems(menuItems)}
      </List>

      <Divider />

      <List sx={{ p: sidebarCollapsed ? 0.5 : 1 }}>
        <Tooltip title={sidebarCollapsed ? "Profile" : ""} placement="right">
          <ListItem
            component="div"
            onClick={handleProfileClick}
            sx={{
              color: theme.palette.text.primary,
              borderRadius: sidebarCollapsed ? "8px" : "0px 8px 8px 0px",
              borderLeft: "3px solid transparent",
              mx: sidebarCollapsed ? 0.5 : 0,
              my: 0.25,
              px: sidebarCollapsed ? 1.5 : 2,
              py: 1.25,
              transition: "all 0.2s ease",
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "rgba(0, 119, 182, 0.06)",
                color: "#0077B6",
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: theme.palette.text.secondary,
                minWidth: sidebarCollapsed ? "auto" : 40,
                mr: sidebarCollapsed ? 0 : 2,
                justifyContent: "center",
              }}
            >
              <AccountCircleIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText
                primary="Profile"
                sx={{
                  fontWeight: 500,
                  "& .MuiTypography-root": { fontSize: "0.95rem" },
                }}
              />
            )}
          </ListItem>
        </Tooltip>

        <Tooltip title={sidebarCollapsed ? "Logout" : ""} placement="right">
          <ListItem
            component="div"
            onClick={handleLogout}
            sx={{
              color: theme.palette.error.main,
              borderRadius: sidebarCollapsed ? "8px" : "0px 8px 8px 0px",
              borderLeft: "3px solid transparent",
              mx: sidebarCollapsed ? 0.5 : 0,
              my: 0.25,
              px: sidebarCollapsed ? 1.5 : 2,
              py: 1.25,
              transition: "all 0.2s ease",
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "rgba(211, 47, 47, 0.06)",
                borderLeft: "3px solid #D32F2F",
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: theme.palette.error.main,
                minWidth: sidebarCollapsed ? "auto" : 40,
                mr: sidebarCollapsed ? 0 : 2,
                justifyContent: "center",
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText
                primary="Logout"
                sx={{
                  fontWeight: 500,
                  "& .MuiTypography-root": { fontSize: "0.95rem" },
                }}
              />
            )}
          </ListItem>
        </Tooltip>
      </List>
    </Box>
  );



  const currentDrawerWidth = isMobile
    ? drawerWidth
    : sidebarCollapsed
      ? collapsedWidth
      : drawerWidth;

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { xs: "100%", md: `calc(100% - ${currentDrawerWidth}px)` },
          left: { xs: 0, md: `${currentDrawerWidth}px` },
          backgroundColor: "#FFFFFF",
          color: theme.palette.text.primary,
          boxShadow: "none",
          borderBottom: "1px solid #E2E8F0",
          transition: "width 0.3s ease, left 0.3s ease",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", minHeight: "70px !important", px: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {isMobile && (
              <IconButton
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                sx={{
                  color: "#5C6F85",
                  "&:hover": {
                    backgroundColor: "rgba(0, 119, 182, 0.06)",
                    color: "#0077B6",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Facility Logo in Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                ml: { xs: 0, md: sidebarCollapsed ? 1 : 0 },
                mr: 1,
              }}
            >
              <img
                src={
                  facilityLogoUrl
                    ? facilityLogoUrl
                    : process.env.PUBLIC_URL + "/assets/JSNU-logo.png"
                }
                alt={`${tenant?.name || "Facility"} Logo`}
                style={{
                  height: isMobile ? "40px" : "48px",
                  width: "auto",
                  maxWidth: isMobile ? "140px" : "200px",
                  objectFit: "contain",
                  display: "block",
                  transition: "all 0.3s ease",
                }}
              />
            </Box>
          </Box>

          {/* Right side: Notification Bell + User */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1 }}>
            <Tooltip title="Help">
              <IconButton
                onClick={() => setHelpOpen(true)}
                sx={{
                  color: "#1A2B45",
                  backgroundColor: "transparent",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.06)",
                    color: "#000000",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <HelpIcon />
              </IconButton>
            </Tooltip>
            {(isAdmin || isSuperAdmin || isFacilityAdmin) && (
              <NotificationDropdown />
            )}
          </Box>
        </Toolbar>
      </AppBar>


      <Box
        component="nav"
        sx={{
          width: { xs: 0, md: currentDrawerWidth },
          flexShrink: 0,
          transition: "width 0.3s ease",
        }}
        aria-label="navigation"
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: isMobile ? drawerWidth : currentDrawerWidth,
              borderRight: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              transition: "width 0.3s ease",
              overflowX: "hidden",
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          minWidth: 0,
          width: { xs: "100%", md: `calc(100% - ${currentDrawerWidth}px)` },
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          transition: "width 0.3s ease, margin-left 0.3s ease",
          position: 'relative',
          overflowX: 'hidden'
        }}
      >
        <Toolbar sx={{ minHeight: "70px !important" }} />
        {children}
      </Box>

      {/* Help / User Manual Modal */}
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            height: '90vh',
            width: { xs: '95vw', sm: '88vw', md: 'calc(90vh * 0.9)' },
            maxWidth: '1200px',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#ffffff',
          borderBottom: '1px solid #E2E8F0',
          p: 1.5,
          px: 2.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DocumentIcon sx={{ color: '#0077B6', fontSize: 20 }} />
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', letterSpacing: '0.3px', color: '#1A2B45' }}>
              User Manual
            </Typography>
          </Box>
          <IconButton onClick={() => setHelpOpen(false)} size="small" sx={{ color: '#5C6F85', '&:hover': { color: '#d32f2f', bgcolor: 'rgba(211,47,47,0.08)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flexGrow: 1, bgcolor: '#525659', overflow: 'hidden' }}>
          <iframe
            src={(process.env.PUBLIC_URL || "") + "/assets/docs/user-manual.pdf#toolbar=0&navpanes=0&scrollbar=0&view=FitH"}
            width="100%"
            height="100%"
            style={{ border: 'none', display: 'block' }}
            title="User Manual"
            onContextMenu={(e) => e.preventDefault()}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AppLayout;